import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import Layout from '../../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { 
  BookOpen, Plus, Edit, Trash2, Eye, RefreshCw, Sparkles, 
  Check, X, Calendar, ArrowLeft, Save, Globe, FileText, Upload, Image
} from 'lucide-react';
import { toast } from 'sonner';
import { useDateFormat } from '../../contexts/DateFormatContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogManagement = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { formatDate } = useDateFormat();
  const fileInputRef = useRef(null);
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Betting Tips',
    tags: [],
    featured_image: '',
    status: 'draft'
  });
  
  // Generate form state
  const [showGenerator, setShowGenerator] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    topic_type: 'betting_tips',
    league: '',
    additional_context: ''
  });
  
  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);

  const categories = [
    'Match Previews',
    'League Analysis', 
    'Betting Tips',
    'Strategy Guide',
    'Weekly Roundup',
    'News & Updates'
  ];

  const topicTypes = [
    { value: 'match_preview', label: 'Match Preview' },
    { value: 'league_analysis', label: 'League Analysis' },
    { value: 'betting_tips', label: 'Betting Tips' },
    { value: 'strategy_guide', label: 'Strategy Guide' },
    { value: 'weekly_roundup', label: 'Weekly Roundup' }
  ];

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchArticles();
  }, [page, statusFilter, user]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      const params = { page, limit: 10 };
      if (statusFilter) params.status = statusFilter;
      
      const response = await axios.get(`${API}/admin/blog/articles`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data.articles);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const token = localStorage.getItem('session_token');
      
      const response = await axios.post(`${API}/admin/blog/generate`, generateForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const generated = response.data.generated;
        setArticleForm({
          title: generated.title,
          slug: generated.slug,
          excerpt: generated.excerpt,
          content: generated.content,
          category: generated.category,
          tags: generated.tags,
          featured_image: '',
          status: 'draft'
        });
        setShowGenerator(false);
        setShowEditor(true);
        toast.success('Content generated! Review and edit before publishing.');
      }
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (publish = false) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('session_token');
      
      const data = {
        ...articleForm,
        status: publish ? 'published' : articleForm.status
      };
      
      if (editingArticle) {
        await axios.put(`${API}/admin/blog/articles/${editingArticle.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Article updated successfully');
      } else {
        await axios.post(`${API}/admin/blog/articles`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(publish ? 'Article published!' : 'Draft saved!');
      }
      
      setShowEditor(false);
      setEditingArticle(null);
      resetForm();
      fetchArticles();
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/admin/blog/articles/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Article deleted');
      setDeleteId(null);
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, GIF, WebP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size: 5MB');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/blog/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Construct full URL
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        setArticleForm({ ...articleForm, featured_image: fullUrl });
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      content: article.content,
      category: article.category,
      tags: article.tags || [],
      featured_image: article.featured_image || '',
      status: article.status
    });
    setShowEditor(true);
  };

  const resetForm = () => {
    setArticleForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'Betting Tips',
      tags: [],
      featured_image: '',
      status: 'draft'
    });
  };

  const handleTagInput = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const tag = e.target.value.trim();
      if (!articleForm.tags.includes(tag)) {
        setArticleForm({ ...articleForm, tags: [...articleForm.tags, tag] });
      }
      e.target.value = '';
    }
  };

  const removeTag = (tagToRemove) => {
    setArticleForm({ 
      ...articleForm, 
      tags: articleForm.tags.filter(tag => tag !== tagToRemove) 
    });
  };

  // Editor View
  if (showEditor) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => { setShowEditor(false); setEditingArticle(null); resetForm(); }}
              className="flex items-center gap-2 text-gray-400 hover:text-white mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Articles
            </button>

            <Card className="glass-dark border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  {editingArticle ? 'Edit Article' : 'New Article'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Title *</Label>
                    <Input
                      value={articleForm.title}
                      onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="Article title"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Slug</Label>
                    <Input
                      value={articleForm.slug}
                      onChange={(e) => setArticleForm({ ...articleForm, slug: e.target.value })}
                      className="bg-white/10 border-white/20 text-white"
                      placeholder="url-friendly-slug"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white">Excerpt *</Label>
                  <Textarea
                    value={articleForm.excerpt}
                    onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="Brief description for article cards"
                    rows={2}
                  />
                </div>

                <div>
                  <Label className="text-white">Content * (Markdown supported)</Label>
                  <Textarea
                    value={articleForm.content}
                    onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                    className="bg-white/10 border-white/20 text-white font-mono text-sm"
                    placeholder="Full article content..."
                    rows={15}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Category</Label>
                    <select
                      value={articleForm.category}
                      onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
                      className="w-full p-2 bg-slate-800 border border-white/20 rounded text-white focus:outline-none focus:border-blue-500"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat} className="bg-slate-800 text-white">{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-white">Featured Image</Label>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={articleForm.featured_image}
                          onChange={(e) => setArticleForm({ ...articleForm, featured_image: e.target.value })}
                          className="bg-white/10 border-white/20 text-white flex-1"
                          placeholder="Image URL or upload below"
                        />
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="hidden"
                        />
                        <Button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/10"
                        >
                          {uploading ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      {articleForm.featured_image && (
                        <div className="relative">
                          <img 
                            src={articleForm.featured_image} 
                            alt="Preview" 
                            className="w-full h-32 object-cover rounded border border-white/20"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          <button
                            type="button"
                            onClick={() => setArticleForm({ ...articleForm, featured_image: '' })}
                            className="absolute top-2 right-2 p-1 bg-red-500 rounded-full hover:bg-red-600"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-white">Tags (Press Enter to add)</Label>
                  <Input
                    onKeyDown={handleTagInput}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="Add tags..."
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {articleForm.tags.map(tag => (
                      <span key={tag} className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 flex items-center gap-1">
                        {tag}
                        <button onClick={() => removeTag(tag)} className="hover:text-red-400">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                  <Button
                    onClick={() => handleSave(false)}
                    disabled={saving || !articleForm.title || !articleForm.content}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Draft
                  </Button>
                  <Button
                    onClick={() => handleSave(true)}
                    disabled={saving || !articleForm.title || !articleForm.content}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {saving ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Globe className="w-4 h-4 mr-2" />
                    )}
                    Publish
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  // Generator Modal
  if (showGenerator) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => setShowGenerator(false)}
              className="flex items-center gap-2 text-gray-400 hover:text-white mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <Card className="glass-dark border-purple-500/30">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-blue-500 to-purple-500" />
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  Generate AI Content
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-white">Content Type</Label>
                  <select
                    value={generateForm.topic_type}
                    onChange={(e) => setGenerateForm({ ...generateForm, topic_type: e.target.value })}
                    className="w-full p-2 bg-slate-800 border border-white/20 rounded text-white focus:outline-none focus:border-blue-500"
                  >
                    {topicTypes.map(type => (
                      <option key={type.value} value={type.value} className="bg-slate-800 text-white">{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label className="text-white">League/Competition (Optional)</Label>
                  <Input
                    value={generateForm.league}
                    onChange={(e) => setGenerateForm({ ...generateForm, league: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="e.g., Premier League, La Liga"
                  />
                </div>

                <div>
                  <Label className="text-white">Additional Context (Optional)</Label>
                  <Textarea
                    value={generateForm.additional_context}
                    onChange={(e) => setGenerateForm({ ...generateForm, additional_context: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="Any specific topics, teams, or angles to cover..."
                    rows={3}
                  />
                </div>

                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="w-full bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white"
                >
                  {generating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Content
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-400 text-center">
                  AI will generate content based on your inputs. You can edit before publishing.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  // Main List View
  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30">
                <BookOpen className="w-8 h-8 text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Blog Management</h1>
                <p className="text-gray-400 text-sm">Create and manage blog articles</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowGenerator(true)}
                className="bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI Generate
              </Button>
              <Button
                onClick={() => { resetForm(); setShowEditor(true); }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Article
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => { setStatusFilter(''); setPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                !statusFilter ? 'bg-blue-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              All
            </button>
            <button
              onClick={() => { setStatusFilter('published'); setPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'published' ? 'bg-green-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              Published
            </button>
            <button
              onClick={() => { setStatusFilter('draft'); setPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === 'draft' ? 'bg-yellow-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              Drafts
            </button>
          </div>

          {/* Articles List */}
          <Card className="glass-dark border-white/10">
            <CardContent className="p-0">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-400 mx-auto"></div>
                  <p className="text-gray-400 mt-4">Loading articles...</p>
                </div>
              ) : articles.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No articles yet. Create your first one!</p>
                </div>
              ) : (
                <div className="divide-y divide-white/10">
                  {articles.map(article => (
                    <div key={article.id} className="p-4 hover:bg-white/5 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-grow">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              article.status === 'published' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {article.status}
                            </span>
                            <span className="text-xs text-gray-500">{article.category}</span>
                          </div>
                          <h3 className="text-white font-semibold mb-1">{article.title}</h3>
                          <p className="text-gray-400 text-sm line-clamp-1">{article.excerpt}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(article.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {article.views} views
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {article.status === 'published' && (
                            <a
                              href={`/blog/${article.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg bg-white/10 text-gray-400 hover:text-white hover:bg-white/20"
                            >
                              <Eye className="w-4 h-4" />
                            </a>
                          )}
                          <button
                            onClick={() => handleEdit(article)}
                            className="p-2 rounded-lg bg-white/10 text-gray-400 hover:text-blue-400 hover:bg-blue-500/20"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteId(article.id)}
                            className="p-2 rounded-lg bg-white/10 text-gray-400 hover:text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Previous
              </Button>
              <span className="px-4 py-2 text-gray-400">
                Page {page} of {pagination.total_pages}
              </span>
              <Button
                onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                disabled={page === pagination.total_pages}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={() => setDeleteId(null)}
        title="Delete Article"
        description="Are you sure you want to delete this article? This action cannot be undone."
        onConfirm={handleDelete}
        confirmText="Delete"
        variant="destructive"
      />
    </Layout>
  );
};

export default BlogManagement;
